import { FC, useCallback, useMemo, useState } from 'react'
import {
    createColumnHelper,
    Row,
    SortingState,
    Table,
} from '@atbay/cyber-components'

import { useDrawer } from '@/core/context/DrawerContext'
import Typography from '@/ui/components/typography'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { Analytics } from '@core/services/mixpanel'
import ChevronRight from '@/assets/images/chevron-right.svg?react'

import {
    Cell,
    ChevronCell,
    EmptyCard,
    TechnologiesTable,
    TechnologiesTableWrapper,
} from './AssociatedTechnologiesTable.styles'
import {
    AssociatedTechnologiesTableProps,
    TAssociatedTechnology,
} from './AssociatedTechnologiesTable.types'

type CellInfo = {
    row: Row<TAssociatedTechnology>
    table: Table<TAssociatedTechnology>
}

const columnHelper = createColumnHelper<TAssociatedTechnology>()

const ROW_HEIGHT = 60
const MAX_VISIBLE_ROWS = 4

const AssociatedTechnologiesTable: FC<AssociatedTechnologiesTableProps> = ({
    technologies,
    dataTestId,
    origin,
}) => {
    const [sorting, setSorting] = useState<SortingState>([])
    const { setDrawerState } = useDrawer()

    const handleRowClick = useCallback(
        (
            row: Row<TAssociatedTechnology>,
            table: Table<TAssociatedTechnology>
        ) => {
            const { sortedIndex, items } = getTableRowContext(row, table)
            const technology = items[sortedIndex]

            Analytics.event('SEM::user_clicks_at_technology', {
                origin,
                technologyId: technology.id,
                technologyName: technology.name,
                technologyType: technology.type,
            })

            setDrawerState((prev) => ({
                ...prev,
                isDrawerOpen: true,
                drawerContentType: 'technologyDetails',
                contentProps: {
                    technologies: items,
                    currentIndex: sortedIndex,
                },
                previousState: {
                    drawerContentType: prev.drawerContentType,
                    contentProps: prev.contentProps,
                    previousState: prev.previousState,
                },
            }))
        },
        [setDrawerState, origin]
    )

    const withRowClick = useCallback(
        (info: CellInfo) =>
            info.row.original.isClickable
                ? {
                      onClick: () => handleRowClick(info.row, info.table),
                      isClickable: true as const,
                  }
                : { isClickable: false as const },
        [handleRowClick]
    )

    const columns = useMemo(
        () => [
            {
                ...columnHelper.accessor('name', {
                    id: 'name',
                    header: () => (
                        <Typography variant="s-regular">Technology</Typography>
                    ),
                    cell: (info) => (
                        <Cell
                            {...withRowClick(info)}
                            data-testid="associated-technology-name"
                        >
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                            >
                                {info.getValue()}
                            </Typography>
                        </Cell>
                    ),
                    enableSorting: true,
                }),
                width: '60%',
            },
            {
                ...columnHelper.accessor('version', {
                    id: 'version',
                    header: () => (
                        <Typography variant="s-regular">Version</Typography>
                    ),
                    cell: (info) => (
                        <Cell
                            {...withRowClick(info)}
                            data-testid="associated-technology-version"
                        >
                            <Typography
                                className="table-cell-text"
                                variant="s-regular"
                            >
                                {info.getValue() || '-'}
                            </Typography>
                        </Cell>
                    ),
                    enableSorting: true,
                }),
                width: '35%',
            },
            {
                id: 'chevron',
                header: () => null,
                cell: (info: CellInfo) =>
                    info.row.original.isClickable ? (
                        <ChevronCell {...withRowClick(info)}>
                            <ChevronRight />
                        </ChevronCell>
                    ) : (
                        <ChevronCell isClickable={false} />
                    ),
                enableSorting: false,
                width: '5%',
            },
        ],
        [withRowClick]
    )

    const visibleRowCount = Math.min(technologies.length, MAX_VISIBLE_ROWS)
    const tableHeight = ROW_HEIGHT * visibleRowCount

    if (technologies.length === 0) {
        return (
            <TechnologiesTableWrapper data-testid={dataTestId}>
                <EmptyCard data-testid="no-associated-technologies">
                    <Typography variant="s-regular" color="inherit">
                        No associated technologies.
                    </Typography>
                </EmptyCard>
            </TechnologiesTableWrapper>
        )
    }

    return (
        <TechnologiesTableWrapper data-testid={dataTestId}>
            <TechnologiesTable
                columns={columns}
                data={technologies}
                sorting={sorting}
                setSorting={setSorting}
                getRowId={(row: TTechnology) => row.id}
                rowHeight={ROW_HEIGHT}
                tableHeight={tableHeight}
            />
        </TechnologiesTableWrapper>
    )
}

export default AssociatedTechnologiesTable
